import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { resolvePhotoUrl } from '../../utils/photoUrl';
import { countShapes, specimenIndexes, validateEntries } from '../../utils/annotationGeometry';
import ConfirmDeleteModal from '../ConfirmDeleteModal';
import AnnotationCanvas from './AnnotationCanvas';

/*
 * The annotation dialog for one photograph: loads the caller's annotations and
 * everyone else's (GET /photos/{id}/annotations), edits the caller's own on the
 * canvas, and saves them as one set (PUT). Unsaved changes are never dropped
 * silently -- closing asks first.
 */
export default function PhotoAnnotator({ photo, onClose }) {
  const { t } = useTranslation('annotate');
  const url = `/photos/${photo.photo_id}/annotations`;
  const [status, setStatus] = useState('loading');
  const [mine, setMine] = useState([]);
  const [others, setOthers] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [showOthers, setShowOthers] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState({ ok: '', error: '' });
  const [confirm, setConfirm] = useState(null);
  const saved = useRef('[]');
  const imageSize = useRef({});

  const adopt = (data) => {
    const entries = data?.mine || [];
    setMine(entries);
    saved.current = JSON.stringify(entries.map(stripMeta));
    setOthers(data?.others || []);
    setCanEdit(Boolean(data?.can_edit));
  };

  useEffect(() => {
    let live = true;
    Promise.resolve(api.get(url))
      .then(res => { if (live) { adopt(res?.data); setStatus('ready'); } })
      .catch(() => { if (live) setStatus('failed'); });
    return () => { live = false; };
  }, [url]);

  const dirty = JSON.stringify(mine.map(stripMeta)) !== saved.current;

  const save = async () => {
    const problem = validateEntries(mine.map(stripMeta));
    if (problem) {
      setMessage({ ok: '', error: t(`problems.${problem}`, { defaultValue: problem }) });
      return;
    }
    setBusy(true);
    setMessage({ ok: '', error: '' });
    try {
      const { data } = await api.put(url, {
        image_width: imageSize.current.width || null,
        image_height: imageSize.current.height || null,
        annotations: mine.map(stripMeta),
      });
      adopt(data);
      setMessage({ ok: t('saved'), error: '' });
    } catch (err) {
      setMessage({ ok: '', error: t('saveFailed', { error: err?.response?.data?.error || '' }) });
    } finally {
      setBusy(false);
    }
  };

  const clearMine = async () => {
    setBusy(true);
    try {
      await api.delete(url);
      setMine([]);
      saved.current = '[]';
      setConfirm(null);
      setMessage({ ok: t('cleared'), error: '' });
    } catch (err) {
      setMessage({ ok: '', error: t('saveFailed', { error: err?.response?.data?.error || '' }) });
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  const requestClose = () => (dirty ? setConfirm('discard') : onClose());
  const otherCount = others.reduce((n, p) => n + p.entries.length, 0);

  return (
    <div style={overlayStyle} onClick={requestClose}>
      <div role="dialog" aria-modal="true" aria-label={t('title')} data-testid="photo-annotator"
           style={dialogStyle} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 17 }}>✏️ {t('title')}</h3>
          <button type="button" onClick={requestClose} aria-label={t('close')} title={t('tip.close')} style={closeStyle}>✕</button>
        </div>
        <p style={mutedStyle}>{t('intro')}</p>

        {status === 'loading' && <p style={mutedStyle}>{t('loading')}</p>}
        {status === 'failed' && <p role="alert" style={errorStyle}>{t('loadFailed')}</p>}
        {status === 'ready' && (
          <>
            {!canEdit && <p role="note" style={noteStyle}>{t('readOnly')}</p>}
            <AnnotationCanvas
              src={resolvePhotoUrl(photo.s3_url)}
              value={mine}
              onChange={setMine}
              others={others}
              showOthers={showOthers || !canEdit}
              readOnly={!canEdit}
              onImageSize={s => { imageSize.current = s; }}
            />
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
              <span data-testid="annotation-summary">
                {mine.length === 0 ? t('empty')
                  : t('summary', { specimens: specimenIndexes(mine).length, shapes: countShapes(mine) })}
              </span>
              {canEdit && otherCount > 0 && (
                <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <input type="checkbox" checked={showOthers} onChange={e => setShowOthers(e.target.checked)} title={t('tip.showOthers')} />
                  {t('showOthers', { count: others.length })}
                </label>
              )}
            </div>
            {message.ok && <p role="status" style={okStyle}>{message.ok}</p>}
            {message.error && <p role="alert" style={errorStyle}>{message.error}</p>}
            {canEdit && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" data-testid="annotation-save" onClick={save} disabled={busy || !dirty} title={t('tip.save')} style={primaryBtn(!busy && dirty)}>
                  {busy ? t('saving') : t('save')}
                </button>
                {saved.current !== '[]' && (
                  <button type="button" onClick={() => setConfirm('clearMine')} disabled={busy} title={t('tip.clearMine')} style={dangerBtn}>
                    {t('clearMine')}
                  </button>
                )}
              </div>
            )}
          </>
        )}

        <ConfirmDeleteModal
          open={confirm === 'discard'}
          title={t('discardTitle')}
          consequences={[t('discardBody')]}
          confirmLabel={t('discard')}
          onConfirm={() => { setConfirm(null); onClose(); }}
          onCancel={() => setConfirm(null)}
        />
        <ConfirmDeleteModal
          open={confirm === 'clearMine'}
          title={t('clearMineTitle')}
          consequences={[t('clearMineBody')]}
          deleting={busy}
          confirmLabel={t('clearMine')}
          confirmingLabel={t('deleting')}
          onConfirm={clearMine}
          onCancel={() => setConfirm(null)}
        />
      </div>
    </div>
  );
}

function stripMeta({ specimen_index: specimenIndex, layer, fertile_type: fertileType, geometry }) {
  return { specimen_index: specimenIndex, layer, fertile_type: fertileType ?? null, geometry };
}

const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 3200, display: 'flex',
                       alignItems: 'center', justifyContent: 'center', padding: 12 };
const dialogStyle = { background: 'var(--c-surface)', color: 'var(--c-text-fg)', borderRadius: 12, padding: 14,
                      width: 'min(980px, 100%)', maxHeight: '96vh', overflow: 'auto', display: 'flex',
                      flexDirection: 'column', gap: 8 };
const mutedStyle = { margin: 0, fontSize: 13, color: 'var(--c-text-muted-fg)' };
const noteStyle = { margin: 0, fontSize: 13, color: 'var(--c-warning-text-fg)', background: 'var(--c-warning-soft)', borderRadius: 6, padding: '6px 10px' };
const okStyle = { margin: 0, fontSize: 13, color: 'var(--c-success-fg)' };
const errorStyle = { margin: 0, fontSize: 13, color: 'var(--c-danger-fg)', background: 'var(--c-danger-soft)', borderRadius: 6, padding: '6px 10px' };
const closeStyle = { background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--c-text-muted-fg)' };
const primaryBtn = enabled => ({ background: enabled ? 'var(--c-primary)' : 'var(--c-text-faint)', color: 'var(--c-on-accent)',
                                 border: 'none', borderRadius: 6, padding: '7px 16px', cursor: enabled ? 'pointer' : 'default', fontSize: 14 });
const dangerBtn = { background: 'var(--c-surface)', color: 'var(--c-danger-2-fg)', border: '1px solid var(--c-danger-border)',
                    borderRadius: 6, padding: '7px 14px', cursor: 'pointer', fontSize: 14 };
